import { createContext, useContext, useMemo } from "react";

import {
  DEFAULT_PIPELINE,
  PIPELINE_KEYS,
  getPipeline,
} from "./pipelineConfig";

/**
 * Which loan pipeline the current subtree is operating on.
 *
 * The default is UNDERWRITING, so every existing screen keeps working with no
 * change — `useUnderwritingHook` simply reads `apiPrefix` from here instead of a
 * hardcoded constant. Wrapping a route in <PipelineProvider pipeline="acquisitions">
 * re-points that same drilldown at `/loan_acquisitions/*`.
 */
const PipelineContext = createContext(DEFAULT_PIPELINE);

export const PipelineProvider = ({
  pipeline = PIPELINE_KEYS.UNDERWRITING,
  children,
}) => {
  const value = useMemo(() => getPipeline(pipeline), [pipeline]);
  return (
    <PipelineContext.Provider value={value}>
      {children}
    </PipelineContext.Provider>
  );
};

/** The active pipeline config: { key, label, apiPrefix, paths, capabilities }. */
export const usePipeline = () => useContext(PipelineContext);

/** Convenience flags for branching UI. */
export const useIsAcquisitions = () =>
  usePipeline().key === PIPELINE_KEYS.ACQUISITIONS;

/** Read a single capability flag, e.g. useCapability("tradeEconomics"). */
export const useCapability = (name) => !!usePipeline().capabilities?.[name];

/**
 * The drilldown route base for the ACTIVE pipeline — "/loan" preclose,
 * "/acquisition" postclose.
 *
 * Anything rendered INSIDE the drilldown that links to another tab must build
 * its URL from this. Hardcoding `/loan/${id}` sends an acquisition to the
 * preclose drilldown, which fetches the deal from /underwriting/* and reports
 * "loan not found" — the id only exists in loan_acquisitions.
 */
export const useDrilldownBase = () => usePipeline().drilldownBase;

/** Full drilldown base for one loan, e.g. "/acquisition/6631…". Empty when the
 *  id isn't loaded yet, so callers can guard on a falsy base. */
export const useDrilldownBaseUrl = (loanId) => {
  const base = useDrilldownBase();
  return loanId ? `${base}/${loanId}` : "";
};

export default PipelineContext;
